import { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { MessageSquare, Send, Save, CheckCircle2, AlertCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SmsProviderSettings() {
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({ enabled: false, account_sid: "", auth_token: "", from_number: "" });
  const [testPhone, setTestPhone] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const load = async () => {
    const res = await axios.get(`${API}/settings/integrations/twilio`);
    setCfg(res.data);
    setForm({ enabled: res.data.enabled, account_sid: res.data.account_sid, auth_token: "", from_number: res.data.from_number });
  };
  useEffect(() => { load().catch(() => toast.error("Could not load text messaging settings")); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/settings/integrations/twilio`, form);
      setCfg(res.data.twilio);
      setForm((f) => ({ ...f, auth_token: "" }));
      toast.success("Text messaging settings saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const test = async () => {
    if (!testPhone.trim()) return toast.error("Enter a mobile number to text");
    setTesting(true);
    try {
      const res = await axios.post(`${API}/settings/integrations/twilio/test`, { phone: testPhone });
      if (res.data.status === "sent") toast.success(`Test text sent to ${res.data.to}`);
      else toast.error(res.data.error || "Test text failed");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Test failed"); }
    finally { setTesting(false); }
  };

  if (!cfg) return null;
  const ready = cfg.configured && cfg.enabled;

  return (
    <Card data-testid="sms-provider-settings">
      <CardHeader>
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center">
              <MessageSquare className="w-5 h-5 text-violet-700" />
            </div>
            <div>
              <CardTitle className="text-lg">Text messaging (SMS)</CardTitle>
              <CardDescription>
                Texts the Release-of-Information signing link when the email fails, or on demand. Uses your Twilio account.
              </CardDescription>
            </div>
          </div>
          <Badge variant={ready ? "default" : "secondary"} className={ready ? "bg-green-500" : ""} data-testid="sms-status-badge">
            {ready ? "Connected" : cfg.platform_fallback ? "Using platform number" : "Not set up"}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between rounded-lg border p-3">
          <div>
            <p className="text-sm font-medium">Enable text messages</p>
            <p className="text-xs text-slate-500">Turn off to stop all outgoing texts without deleting your keys</p>
          </div>
          <Switch checked={form.enabled} onCheckedChange={(v) => setForm({ ...form, enabled: v })} data-testid="sms-enabled-switch" />
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-xs">Twilio Account SID</Label>
            <Input value={form.account_sid} onChange={(e) => setForm({ ...form, account_sid: e.target.value })} placeholder="AC..." className="font-mono text-xs" data-testid="sms-account-sid-input" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Auth Token {cfg.auth_token_set && <span className="text-slate-400">(saved — leave blank to keep)</span>}</Label>
            <Input type="password" value={form.auth_token} onChange={(e) => setForm({ ...form, auth_token: e.target.value })} placeholder={cfg.auth_token_set ? "••••••••••••" : "Your Twilio auth token"} className="font-mono text-xs" data-testid="sms-auth-token-input" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Sending phone number</Label>
            <Input value={form.from_number} onChange={(e) => setForm({ ...form, from_number: e.target.value })} placeholder="+1 414 555 0100" data-testid="sms-from-number-input" />
            <p className="text-[11px] text-slate-500">A Twilio number you own. Find all three values at twilio.com/console.</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button onClick={save} disabled={saving} className="gap-2" data-testid="sms-save-btn"><Save className="w-4 h-4" />{saving ? "Saving..." : "Save"}</Button>
          <div className="flex items-center gap-2 ml-auto">
            <Input value={testPhone} onChange={(e) => setTestPhone(e.target.value)} placeholder="Your mobile for a test text" className="w-56" data-testid="sms-test-phone-input" />
            <Button variant="outline" onClick={test} disabled={testing || (!cfg.configured && !cfg.platform_fallback)} className="gap-2" data-testid="sms-test-btn"><Send className="w-4 h-4" />{testing ? "Sending..." : "Send test"}</Button>
          </div>
        </div>
        {cfg.last_test && (
          <p className={`text-xs flex items-center gap-1 ${cfg.last_test.ok ? "text-green-700" : "text-red-600"}`} data-testid="sms-last-test">
            {cfg.last_test.ok ? <CheckCircle2 className="w-3.5 h-3.5" /> : <AlertCircle className="w-3.5 h-3.5" />}
            Last test {new Date(cfg.last_test.at).toLocaleString()}: {cfg.last_test.ok ? `sent to ${cfg.last_test.to}` : cfg.last_test.error}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
